import { TransportationTypeIcon } from '@/components/icons/TransportationTypeIcon';
import Changeability from '@/components/purchase/card/Changeability';
import type { ChangeBookingFlow } from '@/features/manage/manageActions';
import { TransText } from '@/i18n/trans/text';
import type { BookedTripDTO, LegDTO } from '@/types/dto/agent';
import { getTripOfferRows, isTripChangeDisabled } from '@/utils/changeTrips';
import { cn } from '@/utils/cn';
import { formatDay, formatTime, stripTimeZoneOffset } from '@/utils/dateTime';
import {
  Checkbox,
  Divider,
  Icons,
  Typography,
} from '@packages/webshop-design-system';
import { type FC, type MouseEvent, type ReactNode } from 'react';

interface TripOverviewCardProps {
  trip: BookedTripDTO;
  changeFlow: ChangeBookingFlow;
  isSelected: boolean;
  onToggle: () => void;
}

// A single row in the leg block — either the leg's origin or its destination.
// Both share the same 4-column grid; only the leading icon, stop name, and
// timestamp differ.
interface LegEndpointRowProps {
  icon: ReactNode;
  stopName: string | undefined;
  time: string | undefined;
}

const LegEndpointRow: FC<LegEndpointRowProps> = ({ icon, stopName, time }) => (
  <div className="grid w-full grid-cols-[20px,1fr,40px,40px]">
    {icon}
    <Typography variant="body2-bold">{stopName}</Typography>
    <Typography variant="body2" className="text-neutral">
      {time && formatDay(stripTimeZoneOffset(time))}
    </Typography>
    <Typography variant="body2" className="text-right">
      {formatTime(time)}
    </Typography>
  </div>
);

// True when a click originated from a control we should NOT also treat as a
// row-toggle click (the checkbox itself, or its associated <label>). Without
// this guard the click would fire onToggle twice.
function isCheckboxClick(target: EventTarget): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return Boolean(
    target.closest('input[type="checkbox"]') || target.closest('label')
  );
}

const LegBlock: FC<{ leg: LegDTO; isLast: boolean }> = ({ leg, isLast }) => (
  <>
    <LegEndpointRow
      icon={
        <TransportationTypeIcon ptMode={leg.ptMode} className="text-primary" />
      }
      stopName={leg.originStop?.name}
      time={leg.departureTime}
    />

    <div className="ml-[7px] h-3 w-0.5 bg-neutral-light" />

    <LegEndpointRow
      icon={
        <Icons.destination
          height={12}
          width={12}
          className="ml-0.5 text-primary"
        />
      }
      stopName={leg.destinationStop?.name}
      time={leg.arrivalTime}
    />

    {!isLast && (
      <div className="my-3 flex items-center gap-1">
        <Icons.transfer className="text-neutral" />
        <Typography variant="body2-bold" className="text-neutral">
          <TransText i18nKey="transfer" />
        </Typography>
      </div>
    )}
  </>
);

const TripOverviewCard: FC<TripOverviewCardProps> = ({
  trip,
  changeFlow,
  isSelected,
  onToggle,
}) => {
  const offerRows = getTripOfferRows(trip);
  const isDisabled = isTripChangeDisabled(trip, changeFlow);
  const effectiveSelected = !isDisabled && isSelected;
  const legs = trip.legs ?? [];

  const handleToggle = () => {
    if (isDisabled) return;
    onToggle();
  };

  const handleRowClick = (event: MouseEvent<HTMLDivElement>) => {
    if (isCheckboxClick(event.target)) return;
    handleToggle();
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-md border p-3',
        effectiveSelected
          ? 'border-primary bg-primary-light'
          : 'border-neutral-light',
        isDisabled
          ? 'cursor-not-allowed bg-greyscale-100 hover:border-neutral-light hover:bg-greyscale-100'
          : 'cursor-pointer hover:bg-primary-pale'
      )}
      onClick={handleRowClick}
      data-testid="trip-overview"
      aria-disabled={isDisabled || undefined}
    >
      <div className="flex flex-row items-start gap-3">
        <Checkbox
          isSelected={effectiveSelected}
          onChange={handleToggle}
          isDisabled={isDisabled}
          aria-label={trip.id}
        />
        <div className="flex flex-1 flex-col">
          {legs.map((leg, index) => (
            <LegBlock
              key={leg.id ?? index}
              leg={leg}
              isLast={index === legs.length - 1}
            />
          ))}
        </div>
      </div>

      <Divider variant={effectiveSelected ? 'strong' : 'medium'} />

      <div className="flex flex-col gap-2">
        {offerRows.map((row) => (
          <div key={row.label} className="flex flex-row items-center gap-3">
            <Typography variant="body2" className="flex-1">
              {row.label}
            </Typography>
            <Changeability condition={row.isExchangeable} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default TripOverviewCard;
